import { useEffect, useState } from "react";
import {
    RouterProvider,
    createBrowserRouter,
} from "react-router-dom";
import { Socket, io } from "socket.io-client";
import { QueryClient, QueryClientProvider } from "react-query";
import { CastReceiverContext } from "chromecast-caf-receiver/cast.framework";

import "@app/i18n";

import Game from './screens/Game';
import Lobby from './screens/Lobby';
import EndGame from './screens/EndGame';
import Connection from "./screens/Connection";

import { QUIZZ_SOCKET_URL, API_PATH } from "./config";

import Context from "./contexts/Context";

import { RoomType } from "./types/room";
import { Locales } from "./types/question";

import { setLanguage } from "./i18n/languageStorage";

const CUSTOM_NAMESPACE = "urn:x-cast:com.mmc.poc.playzer.v1";
export const deviceType = process.env.REACT_APP_DEVICE as "cast" | "web";

const queryClient = new QueryClient();

export interface DefaultRouteProps {
    receiverContext?: CastReceiverContext;
}

export default function App() {
    const [ioSocket, setIoSocket] = useState<Socket>();
    const [roomId, setRoomId] = useState<string>();
    const [roomType, setRoomType] = useState<RoomType>();

    let receiverContext: CastReceiverContext | undefined = undefined;

    if (deviceType === "cast") {
        receiverContext = cast.framework.CastReceiverContext.getInstance();
    }
    useEffect(() => {
        if (!roomId && receiverContext) {
            receiverContext.addCustomMessageListener(CUSTOM_NAMESPACE, function(event) {
                const pathApi = API_PATH ?? "";
                const socket = io(QUIZZ_SOCKET_URL, {
                    path: pathApi + "/socket.io",
                    transports: ["polling"],
                    forceNew: true,
                    withCredentials: true,
                    extraHeaders: { "room": `${event.data.roomId}` }}
                );
                socket.connect();
                setIoSocket(socket);
                setRoomId(event.data.roomId.split("-")[1]);
                setLanguage(event.data.locale);
            });
            receiverContext.start({ disableIdleTimeout: true });
        }
    }, [receiverContext, roomId]);

    useEffect(() => {
        const localeTest = new Intl.Locale(navigator.language);
        setLanguage(localeTest.language as Locales);
    }, []);

    const router = createBrowserRouter([
        {
            path: "/",
            element: <Connection />,
        },
        {
            path: "/lobby",
            element: <Lobby receiverContext={receiverContext}/>,
        },
        {
            path: "/game",
            element: <Game receiverContext={receiverContext}/>,
        },
        {
            path: "/game-ended/:winSoundNumber",
            element: <EndGame receiverContext={receiverContext}/>,
        },
    ]);

    const context = {
        ioSocket,
        setIoSocket,
        roomId,
        setRoomId,
        roomType,
        setRoomType,
      };

    return (
        <QueryClientProvider client={queryClient}>
            <Context.Provider value={context}>
                <RouterProvider router={router} />
            </Context.Provider>
        </QueryClientProvider>
    );
};
